import { useTranslation } from 'react-i18next';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import type { CalendarView } from '../../types/calendar';

interface CalendarHeaderProps {
  view: CalendarView;
  selectedDate: Date;
  onViewChange: (view: CalendarView) => void;
  onDateChange: (date: Date) => void;
  onNavigate: (direction: 'prev' | 'next' | 'today') => void;
}

/**
 * CalendarHeader component
 * Displays navigation controls, view toggle, and current date for the calendar
 */
export default function CalendarHeader({
  view,
  selectedDate,
  onViewChange,
  onDateChange: _onDateChange,
  onNavigate,
}: CalendarHeaderProps) {
  // Note: _onDateChange is available for future date picker functionality
  void _onDateChange;
  const { t } = useTranslation();

  /**
   * Format the date display based on current view
   */
  const getDateDisplay = (): string => {
    switch (view) {
      case 'day':
        return format(selectedDate, 'EEEE, MMMM d, yyyy');
      case 'week': {
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
        // Same month
        if (format(weekStart, 'MMMM yyyy') === format(weekEnd, 'MMMM yyyy')) {
          return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'd, yyyy')}`;
        }
        // Different months, same year
        if (format(weekStart, 'yyyy') === format(weekEnd, 'yyyy')) {
          return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
        }
        // Different years
        return `${format(weekStart, 'MMM d, yyyy')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      }
      case 'month':
        return format(selectedDate, 'MMMM yyyy');
      default:
        return format(selectedDate, 'MMMM yyyy');
    }
  };

  /**
   * Check if selected date is today
   */
  const isToday = (): boolean => {
    const today = new Date();
    return (
      selectedDate.getDate() === today.getDate() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear()
    );
  };

  const views: CalendarView[] = ['day', 'week', 'month'];

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
      {/* Date Display and Navigation */}
      <div className="flex items-center gap-3">
        {/* Previous Button */}
        <button
          onClick={() => onNavigate('prev')}
          aria-label={t('calendar.navigation.previous')}
          className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-black dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg
            className="w-5 h-5 text-black dark:text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* Date Display */}
        <div className="min-w-[200px] text-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {getDateDisplay()}
          </h2>
        </div>

        {/* Next Button */}
        <button
          onClick={() => onNavigate('next')}
          aria-label={t('calendar.navigation.next')}
          className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-black dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg
            className="w-5 h-5 text-black dark:text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Today Button */}
        <button
          onClick={() => onNavigate('today')}
          disabled={isToday()}
          className={`px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all ${
            isToday()
              ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              : 'border-black dark:border-gray-600 bg-white dark:bg-gray-800 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          {t('calendar.navigation.today')}
        </button>
      </div>

      {/* View Toggle */}
      <div className="flex items-center">
        <div className="inline-flex rounded-lg border-2 border-black dark:border-gray-600 overflow-hidden">
          {views.map((viewOption) => (
            <button
              key={viewOption}
              onClick={() => onViewChange(viewOption)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                view === viewOption
                  ? 'bg-black dark:bg-white text-white dark:text-black'
                  : 'bg-white dark:bg-gray-800 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${viewOption !== 'day' ? 'border-l-2 border-black dark:border-gray-600' : ''}`}
            >
              {t(`calendar.views.${viewOption}`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
